<template>
  <div class='domain-insights'>
    <div class='no-data' v-if='noData'>
      <div class='no-data__title'>
        {{ $t('domain.insights.no.results.found.title') }}
      </div>
      <div class='no-data__subtitle'>
        {{ $t('domain.insights.no.results.found.subtitle') }}
      </div>
      <div class='no-data__initiate-test'>
        <v-btn class="initiate-scan-btn" @click="initiateDiscoveryTest" :disabled='initiateTestClicked'>
          {{ $t('configuration.initiate.test') }}
        </v-btn>
        <span class="scan-initiated" v-if='testInitiated'>
            {{ $t('test.initiated.successfully') }}
          </span>
      </div>
    </div>
    <div v-else class='domain-insights__content'>
      <div v-if='hasInsightsData' class='domain-insights__content'>
        <!-- ###########################  -->
        <!-- CVE Attack Path Visualization - Moved to top, hidden when no CVEs -->
        <threat-visualizer
          v-if="hasCves && !isLoading"
          :key="domain"
          :insights-data="data?.data?.insights?.insightsData"
          :asset-name="domain"
          :asset-correlation="assetCorrelation"
          :is-loading="isLoading"
        />
        <!-- ###########################  -->
        <div class='domain-insights__subtitle'>{{$t('domain.insights.summary')}}</div>
        <p>{{summaryFormatted}}</p>
        <!-- ###########################  -->
        <div class='domain-insights__subtitle'>{{$t('domain.insights.threat.actor.view')}}</div>
        <div class='domain-insights__table'>
          <div class='domain-insights__table__header-row'>
            <div class='header-cell c-15'>{{$t('domain.insights.table.header.vector')}}</div>
            <div class='header-cell c-85'>{{$t('domain.insights.table.header.damage')}}</div>
          </div>
          <div v-for='(data, index) in attackVectorsFormatted' :key='`attack-vectors-row-${index}`' class='domain-insights__table__row extra-height'>
            <div class='table-cell vertical c-15'>
              <div class='table-cell__title'>{{formatVector(data).title}}</div>
              <custom-tooltip class="attack-vectors-row-tooltip" placement="top" popper-class="light-tooltip">
                <div slot='buttonContent'>
                  <div class='table-cell__subtitle'>{{formatVector(data).subtitle}}</div>
                </div>
                <div slot="content">
                  <slot name="tooltip">
                    {{formatVector(data).subtitle}}
                  </slot>
                </div>
              </custom-tooltip>
            </div>
            <custom-tooltip class="damage-cell-tooltip" placement="top" popper-class="light-tooltip damage-tooltip">
              <div slot='buttonContent'>
                <div class='table-cell c-85 damage-cell'>{{data.potential_damage}}</div>
              </div>
              <div slot="content">
                <slot name="tooltip">
                  <div class="damage-tooltip-content">{{data.potential_damage}}</div>
                </slot>
              </div>
            </custom-tooltip>
          </div>
        </div>
        <!-- ###########################  -->
        <div class='domain-insights__subtitle'>{{$t('domain.insights.apt.groups')}}</div>
        <div class='domain-insights__table'>
          <div class='domain-insights__table__header-row'>
            <div class='header-cell c-20'>{{$t('domain.insights.table.header.apt')}}</div>
            <div class='header-cell c-40 centered'>{{$t('domain.insights.table.header.ttps')}}</div>
            <div class='header-cell c-40 centered'>{{$t('domain.insights.table.header.risk')}}</div>
          </div>
          <div v-for='(data, index) in filteredAptGroups' :key='`attack-vectors-row-${index}`' class='apt-row-flip-container'>
            <!-- Front Side - Normal Row -->
            <div class='domain-insights__table__row extra-row-15' :class="{ 'flipped': flippedGroups[getAPTName(data)] }">
              <div class='table-cell space-between c-20'>
                <div class='apt-name'>{{getAPTName(data)}}</div>
                <div class='apt-name-actions'>
                  <div v-if='hasLayer' class='apt-colors'>
                    <div class='color-circle'
                         :style='`background-color:${getAPTColor(data)}80; border: 2px solid ${getAPTColor(data)}`'/>
                  </div>
                  <v-btn
                    v-if='hasReasoning(data)'
                    icon
                    x-small
                    class="reasoning-btn"
                    @click="toggleGroupReasoning(getAPTName(data))"
                  >
                    <v-icon small color="#00f5ff">mdi-lightbulb-on-outline</v-icon>
                  </v-btn>
                </div>
              </div>
              <div class='table-cell centered c-40'>
                <custom-tooltip popper-class="light-tooltip" v-for='(ttp, ttpIndex) in data.TTPs' :key='`${getAPTName(data)}-ttp-${ttpIndex}`'>
                  <div slot='buttonContent'>
                    <v-chip
                      class='ttp-chip'
                      small
                    >
                      {{ttpFormatted(ttp)}}
                    </v-chip>
                  </div>
                  <div slot="content">
                    <slot name="tooltip">
                      {{ ttp }}
                    </slot>
                  </div>
                </custom-tooltip>
              </div>
              <div class='table-cell c-40 centered'>
                <epss-score :value='Number.parseInt(getAPTRiskData(data)["12_months"].split("%")[0])' />
              </div>
            </div>
            <!-- Back Side - Reasoning Card -->
            <div v-if='hasReasoning(data)' class='apt-reasoning-card' :class="{ 'visible': flippedGroups[getAPTName(data)] }">
              <div class='reasoning-card-header'>
                <div class='reasoning-card-title'>
                  <v-icon small color="#00f5ff" class="mr-2">mdi-lightbulb-on-outline</v-icon>
                  <span>{{ getAPTName(data) }} - Threat Assessment</span>
                </div>
                <v-btn icon x-small @click="toggleGroupReasoning(getAPTName(data))">
                  <v-icon small>mdi-close</v-icon>
                </v-btn>
              </div>
              <div class='reasoning-card-content'>
                <!-- Threat Level Score -->
                <div class='reasoning-section'>
                  <div class='reasoning-section-title'>Threat Level Score</div>
                  <div class='threat-score-bar'>
                    <div class='threat-score-fill' :style="{ width: `${(data.reasoning?.threat_level_score || 0) * 100}%` }" />
                    <span class='threat-score-value'>{{ Math.round((data.reasoning?.threat_level_score || 0) * 100) }}%</span>
                  </div>
                </div>
                <!-- TTP Overlap Stats -->
                <div class='reasoning-section'>
                  <div class='reasoning-section-title'>TTP Overlap Analysis</div>
                  <div class='overlap-stats-grid'>
                    <div class='overlap-stat-item' v-if='data.reasoning?.ttp_basic_overlap'>
                      <span class='overlap-label'>Basic</span>
                      <span class='overlap-value'>{{ data.reasoning.ttp_basic_overlap }}</span>
                    </div>
                    <div class='overlap-stat-item' v-if='data.reasoning?.ttp_mature_overlap'>
                      <span class='overlap-label'>Mature</span>
                      <span class='overlap-value'>{{ data.reasoning.ttp_mature_overlap }}</span>
                    </div>
                    <div class='overlap-stat-item' v-if='data.reasoning?.ttp_recent_overlap'>
                      <span class='overlap-label'>Recent</span>
                      <span class='overlap-value'>{{ data.reasoning.ttp_recent_overlap }}</span>
                    </div>
                    <div class='overlap-stat-item' v-if='data.reasoning?.ttp_sophisticated_overlap'>
                      <span class='overlap-label'>Sophisticated</span>
                      <span class='overlap-value'>{{ data.reasoning.ttp_sophisticated_overlap }}</span>
                    </div>
                  </div>
                </div>
                <!-- Reasoning Summary -->
                <div class='reasoning-section reasoning-summary-section' v-if='data.reasoning?.reasoning_summary'>
                  <div class='reasoning-section-title'>Analysis Summary</div>
                  <div class='reasoning-summary-text' v-html='formatReasoningSummary(data.reasoning.reasoning_summary)' />
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- ###########################  -->
        <div class='domain-insights__section-header'>
          <div class='domain-insights__subtitle'>{{$t('domain.insights.mitre')}}</div>
          <div class='domain-insights__section-header__controls'>
            <custom-tooltip popper-class="light-tooltip">
              <div slot='buttonContent'>
                <div @mouseenter='animationMouseEnter("linkAnimation")' class='control-animation-wrapper' @click.stop='openNavigator'>
                  <lottie-animation
                    class='control-animation'
                    :loop='true'
                    :animationData="require('@/assets/icons/animations/link.json')"
                    ref="linkAnimation"
                    @loopComplete="loopComplete('linkAnimation')"
                  />
                </div>
              </div>
              <div slot="content">
                <slot name="tooltip">
                  {{ $t('domain.insights.mitre.attck.navigator.tooltip') }}
                </slot>
              </div>
            </custom-tooltip>
            <custom-tooltip popper-class="light-tooltip">
              <div slot='buttonContent'>
                <div @mouseenter='animationMouseEnter("jsonAnimation")' @click.stop='downloadMITRELayer' class='control-animation-wrapper'>
                  <lottie-animation
                    class='control-animation'
                    :loop='true'
                    :animationData="require('@/assets/icons/animations/json-file.json')"
                    ref="jsonAnimation"
                    @loopComplete="loopComplete('jsonAnimation')"
                  />
                </div>
              </div>
              <div slot="content">
                <slot name="tooltip">
                  {{ $t('domain.insights.mitre.attck.navigator.download.layer.tooltip') }}
                </slot>
              </div>
            </custom-tooltip>
          </div>
        </div>
        <div class='domain-insights__apt-group-select'>
          <v-select
            dense
            outlined
            :value='selectedGroup'
            :items='flatAptGroups'
            v-model = 'selectedGroup'
          >
            <template v-slot:item="data">
                <div class='domain-insights__apt-group-select__item'>
                  <div class='color-circle'
                       :style='`background-color:${flatAptGroupsColorMap[data.item]}80; border: 2px solid ${flatAptGroupsColorMap[data.item]}`'/>
                  <div>
                    {{ data.item }}
                  </div>
                </div>
            </template>
          </v-select>
        </div>
        <div class='domain-insights__table-wrapper' :style="`max-width: ${maxWidth}px !important;`">
          <div class="domain-insights__mitre-scroll-area" :style="`width: ${maxWidth}px !important;`">
            <column-tree-table :data='mitreData' :layer='mitre' :focus='selectedGroup' :colorMap='flatAptGroupsColorMap' :data-version="dataVersion"/>
          </div>
        </div>
      </div>
      <!-- ########################### -->
      <div class='domain-insights__header' v-if='hasDNSTwistData'>
        <div class='domain-insights__header__left'>
          <div class='domain-insights__header__left__subtitle'>{{$t('domain.insights.twisted.domains')}}</div>
          <div class="domain-insights__header__left__switch">
            <v-switch
              v-model="hasSsdeep"
              :label="$t('domain.insights.switch.ssdeep')"
            ></v-switch>
          </div>
        </div>
        <div class='domain-insights__header__right'>
          <custom-tooltip popper-class="light-tooltip">
            <div slot='buttonContent'>
              <div @mouseenter='animationMouseEnter("twistedJsonAnimation")' @click.stop='downloadTwistedDomainsJson' class='domain-insights__header__right__json'>
                <lottie-animation
                  class='control-animation'
                  :loop='true'
                  :animationData="require('@/assets/icons/animations/json-file.json')"
                  ref="twistedJsonAnimation"
                  @loopComplete="loopComplete('twistedJsonAnimation')"
                />
              </div>
            </div>
            <div slot="content">
              <slot name="tooltip">
                {{ $t('domain.insights.twisted.domains.download.json.tooltip') }}
              </slot>
            </div>
          </custom-tooltip>
          <custom-tooltip popper-class="light-tooltip">
            <div slot='buttonContent'>
              <download-c-s-v class="domain-insights__header__right__csv" @click="downloadCSVReport"/>
            </div>
            <div slot="content">
              <slot name="tooltip">
                {{ $t('domain.insights.twisted.domains.download.csv.tooltip') }}
              </slot>
            </div>
          </custom-tooltip>
        </div>
      </div>
      <div v-if='hasDNSTwistData' class='domain-insights__table'>
        <div class='domain-insights__table__header-row'>
          <div class='header-cell c-18'>{{$t('domain.insights.table.header.domain')}}</div>
          <div class='header-cell c-18'>{{$t('domain.insights.table.header.type')}}</div>
          <div class='header-cell c-18'>{{$t('domain.insights.table.header.ip.addr')}}</div>
          <div class='header-cell c-10'>{{$t('domain.insights.table.header.screenshot')}}</div>
          <div class='header-cell c-18 centered l-10'>{{$t('domain.insights.table.header.ssdeep')}}</div>
          <div class='header-cell c-18 r-6'>{{$t('domain.insights.table.header.geo')}}</div>
        </div>
        <div v-for='(data, index) in twistedDomainsFiltered' :key='`attack-vectors-row-${index}`' class='domain-insights__table__row'>
          <div class='table-cell c-18'>
            <v-badge
              tile
              overlap
              :icon="badgeIcon"
              :content="badgeContent"
              class="manual-config-badge"
              :class="{'copied-badge': displayedCopiedBadge.domain[index]}"
              offset-y='4px'
              :value="displayedCopiedBadge.domain[index]"
            >
              <div class="domain-data-text" @click.stop='copyAsset(data.domain, index, "domain")'> {{data.domain}} </div>
            </v-badge>
          </div>
          <div class='table-cell c-18'>{{data.fuzzer.charAt(0).toUpperCase() + data.fuzzer.slice(1).toLowerCase()}}</div>
          <div class='table-cell c-18 twisted-domains-ip-list'>
            <v-badge
              tile
              overlap
              :icon="badgeIcon"
              :content="badgeContent"
              class="manual-config-badge"
              :class="{'copied-badge': displayedCopiedBadge.ip[index]}"
              offset-y='4px'
              :value="displayedCopiedBadge.ip[index]"
            >
              <div class="ip-data-text" @click.stop='copyAsset(data.dns_a.join(", "), index, "ip")'> {{ data.dns_a.join('\n') }} </div>
            </v-badge>
          </div>
          <div class='table-cell c-10 screenshot-cell'>
            <div v-if='data.screenshot' class='screenshot-thumbnail' @click='openScreenshot(data)'>
              <v-img
                :src='getScreenshotThumbnailUrl(data, index)'
                :max-width='95'
                :min-width='95'
                :min-height='75'
                :max-height='75'
                position='top center'
              />
            </div>
            <no-photo-placeholder v-else />
          </div>
          <div class='table-cell c-18 centered'>
            <epss-score :value='data.ssdeep' />
          </div>
          <div class='table-cell c-18 test'>
            <div class='domain-insights__table__row__flag'>
              <country-flag
                rounded
                :country='getGeoLocation(data.geoip || "")'
                size='normal' />
            </div>
            <div class='domain-insights__table__row__country'>
              {{data.geoip}}
            </div>
          </div>
        </div>
      </div>
    </div>
    <v-dialog
      v-model='screenshotPopup.visible'
      max-width='1200'
      content-class='dns-twist-screenshot-dialog'
    >
      <v-card class='screenshot-popup-card'>
        <div class='screenshot-popup-header'>
          <span class='screenshot-domain'>{{ screenshotPopup.domain }}</span>
          <span class='close-btn' @click='closeScreenshotPopup'>
            <span class='material-icons-outlined'>close</span>
          </span>
        </div>
        <div class='screenshot-content'>
          <img v-if='screenshotPopup.url' :src='screenshotPopup.url' />
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import EpssScore from '@/components/common/EpssScore.vue';
import mitreData from '@/assets/mitre/enterprise-attack-reduced.json';
import ColumnTreeTable from '@/components/ColumnTreeTable/ColumnTreeTable.vue';
import PerfectScrollbar from 'perfect-scrollbar';
import 'perfect-scrollbar/css/perfect-scrollbar.css';
import LottieAnimation from 'lottie-web-vue';
import CustomTooltip from '@/components/common/CustomTooltip.vue';
import moment from 'moment/moment';
import CountryFlag from 'vue-country-flag';
import WorldGeoJson from '@/assets/maps/world.geojson';
import DownloadCSV from '@/assets/icons/download_csv.svg?inline';
import NoPhotoPlaceholder from '@/components/common/NoPhotoPlaceholder.vue';
import { ThreatVisualizer } from '@/components/insights/threat-visualizer';
import { TEST_REPEAT_STATUS, TEST_TYPES_TLDS } from '@/constants/projects';
import { ADD_NEW_TEST_PROJECT, INITIATE_TEST_CARD } from '@/store/project/actionTypes';
import { getGroupedTimezonesList } from '@/utils/dateUtils';
import { rules } from '@/utils/validationUtils';
import { getFileUrl } from '@/amplify/auth';

const getTimeWithHourOffset = offset => moment()
  .startOf('hour')
  .add(offset, 'hour')
  .format('HH:mm');

const getTimeWithMinuteOffset = offset => moment()
  .startOf('minute')
  .add(offset, 'minute')
  .format('HH:mm');

export default {
  name: 'DomainInsights',
  components: {
    ColumnTreeTable, EpssScore, LottieAnimation, CountryFlag, CustomTooltip, DownloadCSV, NoPhotoPlaceholder, ThreatVisualizer,
  },
  props: {
    domain: {
      type: String,
      required: true,
    },
    data: {
      type: Object,
      required: false,
    },
    maxWidth: {
      type: Number,
      required: false,
      default: 1730,
    },
    assetCorrelation: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      mitreData,
      testInitiated: false,
      initiateTestClicked: false,
      scrollbarSettings: {
        suppressScrollY: true,
        suppressScrollX: false,
        wheelPropagation: false,
      },
      scrollbars: null,
      hasSsdeep: false,
      selectedGroup: '',
      displayedCopiedBadge: {domain: [], ip: []},
      screenshotUrls: {},
      screenshotPopup: { visible: false, url: null, domain: null },
      flippedGroups: {},
    };
  },
  mounted() {
    this.updateScrollBar();
    [this.selectedGroup] = this.flatAptGroups;
  },
  watch: {
    maxWidth() {
      this.updateScrollBar();
    },
    twistedDomainsFiltered(newTwistedDomains) {
      if (newTwistedDomains.length !== this.displayedCopiedBadge.length) {
        this.initializeBadgeData();
      }
    },
    flatAptGroups: {
      handler(newVal) {
        if (newVal && newVal.length > 0 && !this.selectedGroup) {
          [this.selectedGroup] = newVal;
        }
      },
      immediate: true,
    },
  },
  computed: {
    badgeIcon() {
      return 'build';
    },
    badgeContent() {
      return this.$t('copied');
    },
    noData() {
      return !this.hasInsightsData && !this.hasDNSTwistData;
    },
    dataVersion() {
      // Check for v1 data structure (old format with estimated_percentage_of_getting_hacked at root insights level)
      if (this.data?.data?.insights?.insightsData?.insights?.estimated_percentage_of_getting_hacked &&
          typeof this.data?.data?.insights?.insightsData?.insights?.estimated_percentage_of_getting_hacked === 'object' &&
          !Array.isArray(this.data?.data?.insights?.insightsData?.insights?.estimated_percentage_of_getting_hacked)) {
        return 1;
      }
      // Check for v2 data structure (new format with groups array and relevant_APT_groups)
      if (this.data?.data?.insights?.insightsData?.groups ||
          (this.data?.data?.insights?.insightsData?.insights?.relevant_APT_groups &&
           Array.isArray(this.data?.data?.insights?.insightsData?.insights?.relevant_APT_groups))) {
        return 2;
      }
      return 0;
    },
    hasInsightsData() {
      if (this.dataVersion === 1) {
        return Object.keys(this.data?.data?.insights?.insightsData?.insights?.estimated_percentage_of_getting_hacked || {}).length > 0 &&
          this.aptGroups.length > 0;
      }
      if (this.dataVersion === 2) {
        return this.data?.data?.insights?.insightsData?.insights?.relevant_APT_groups.length > 0 &&
            this.data?.data?.insights?.insightsData?.insights?.threat_actors_view.length > 0;
      }
      return false;
    },
    hasCves() {
      const orgCves = this.data?.data?.insights?.insightsData?.organization?.org_cves || [];
      return orgCves.some(c => c.name && c.name.startsWith('CVE-'));
    },
    hasDNSTwistData() {
      return Object.keys(this.twistedDomainsFormatted || {}).length > 0;
    },
    hasLayer() {
      return Object.keys(this.data?.data?.insights?.insightsData?.mitre || {}).length > 0;
    },
    summaryFormatted() {
      return this.data?.data?.insights?.insightsData?.insights?.summary;
    },
    attackVectorsFormatted() {
      if (this.dataVersion === 1) {
        return this.data?.data?.insights?.insightsData?.insights?.threat_actors_view?.top_5_critical_attack_vectors;
      }
      if (this.dataVersion === 2) {
        return this.data?.data?.insights?.insightsData?.insights?.threat_actors_view;
      }
      return [];
    },
    cveIdToTitleMap() {
      // Create a mapping from CVE ID to CVE title from organization.org_cves
      const map = {};
      const orgCves = this.data?.data?.insights?.insightsData?.organization?.org_cves || [];
      orgCves.forEach(cve => {
        if (cve.name && cve.title) {
          map[cve.name] = cve.title;
        }
      });
      return map;
    },
    aptGroups() {
      return this.data?.data?.insights?.insightsData?.insights?.relevant_APT_groups &&
      this.data?.data?.insights?.insightsData?.insights?.relevant_APT_groups !== 'N/A'
        ? this.data?.data?.insights?.insightsData?.insights?.relevant_APT_groups
        : [];
    },
    aptRisk() {
      if (this.dataVersion === 1) {
        return this.data?.data?.insights?.insightsData?.insights?.estimated_percentage_of_getting_hacked;
      }
      return [];
    },
    filteredAptGroups() {
      if (this.dataVersion === 2) {
        return this.aptGroups;
      }
      const regex = /APT\s*\d+/;
      const filteredAptGroups = this.aptGroups.map(obj => {
        const match = obj.APT_group.match(regex);
        let newAPTGroup = obj.APT_group;
        if (match) {
          [newAPTGroup] = match;
        }
        return { ...obj, APT_group: newAPTGroup, APT_group_display_name: obj.APT_group };
      });
      return filteredAptGroups;
    },
    flatAptGroups() {
      if (this.dataVersion === 1) {
        return Array.from(this.filteredAptGroups, value => value.APT_group_display_name);
      }
      if (this.dataVersion === 2) {
        return Array.from(this.filteredAptGroups, value => {
          const aptName = value['APT name'];
          // Extract name from "G#### (Name)" format - return just "Name"
          const match = aptName.match(/\(([^)]+)\)/);
          return match ? match[1] : aptName;
        });
      }
      return [];
    },
    flatAptGroupsColorMap() {
      const colorsMap = {};
      if (this.dataVersion === 1) {
        const colors = ['#2baf69', '#4d9feb', '#ffa600', '#DF399F', '#24BEB5'];
        this.filteredAptGroups.forEach((value, index) => {
          colorsMap[`${value.APT_group_display_name}`] = colors[index];
        });
      }
      if (this.dataVersion === 2) {
        this.filteredAptGroups.forEach(value => {
          const aptName = value['APT name'];
          // Extract name from "G#### (Name)" format - use just "Name" as key
          const match = aptName.match(/\(([^)]+)\)/);
          const displayName = match ? match[1] : aptName;
          colorsMap[displayName] = value.color;
        });
      }
      return colorsMap;
    },
    twistedDomainsData() {
      return this.data?.data?.dnsTwist?.dnsTwistData?.output || [];
    },
    dnsTwistActivityId() {
      return this.data?.data?.dnsTwist?.activityId || '';
    },
    twistedDomainsFormatted() {
      const validData = this.twistedDomainsData?.filter(record => ('dns_a' in record) && this.isValid(record.dns_a));
      return validData?.map(originalItem => {
        const item = { ...originalItem };
        if (!('ssdeep' in item)) {
          item.ssdeep = 0;
        }
        if ('fuzzer' in item) {
          item.fuzzer = item.fuzzer.replace('*', '');
          if (item.fuzzer === 'original') {
            item.ssdeep = 100;
          }
        }
        return item;
      });
    },
    twistedDomainsFiltered() {
      const formattedData = this.twistedDomainsFormatted;
      const sortedData = formattedData?.sort((a, b) => {
        const ssdeepA = a.ssdeep || 0;
        const ssdeepB = b.ssdeep || 0;

        return ssdeepB - ssdeepA;
      });
      return this.hasSsdeep ? sortedData.filter(item => item.ssdeep > 0) : sortedData;
    },
    mitre() {
      if (this.dataVersion === 2) {
        const techniques = this.data?.data?.insights?.insightsData?.mitre?.techniques || [];
        const selectedColor = this.flatAptGroupsColorMap[this.selectedGroup];
        return techniques.filter(t => t.color === selectedColor);
      }
      return this.data?.data?.insights?.insightsData?.mitre?.techniques || [];
    },
    formattedTimeZone() {
      return getGroupedTimezonesList().map(tz => ({
        value: tz.offset,
        name: tz.name,
        id: tz.id,
      }));
    },
  },
  created() {
    this.initializeBadgeData();
  },
  methods: {
    initializeBadgeData() {
      this.displayedCopiedBadge.domain = Array(this.twistedDomainsFiltered.length).fill(false);
      this.displayedCopiedBadge.ip = Array(this.twistedDomainsFiltered.length).fill(false);
    },
    getScreenshotThumbnailUrl(data, index) {
      const cacheKey = `thumb_${index}`;
      if (this.screenshotUrls[cacheKey]) {
        return this.screenshotUrls[cacheKey];
      }
      if (!data.screenshot) return null;
      const activityId = this.dnsTwistActivityId;
      const thumbnailPath = activityId
        ? `${activityId}/dnstwist_thumbnail_${data.screenshot}`
        : `dnstwist_thumbnail_${data.screenshot}`;
      this.loadScreenshotUrl(thumbnailPath, cacheKey);
      return null;
    },
    async loadScreenshotUrl(path, cacheKey) {
      try {
        const url = await getFileUrl(path);
        this.$set(this.screenshotUrls, cacheKey, url);
      } catch (e) {
        console.error('Failed to load screenshot:', e);
      }
    },
    async openScreenshot(data) {
      if (!data.screenshot) return;
      const activityId = this.dnsTwistActivityId;
      const fullPath = activityId
        ? `${activityId}/dnstwist_${data.screenshot}`
        : `dnstwist_${data.screenshot}`;
      try {
        const url = await getFileUrl(fullPath);
        this.screenshotPopup = {
          visible: true,
          url,
          domain: data.domain,
        };
      } catch (e) {
        console.error('Failed to open screenshot:', e);
      }
    },
    closeScreenshotPopup() {
      this.screenshotPopup = { visible: false, url: null, domain: null };
    },
    async copyAsset(asset, index, type) {
      this.$clipboard(asset);
      this.$set(this.displayedCopiedBadge[type], index, true);
      await new Promise(resolve => setTimeout(resolve, 2000));
      this.$set(this.displayedCopiedBadge[type], index, false);
    },
    openNavigator() {
      window.open('https://mitre-attack.github.io/attack-navigator/', '_blank');
    },
    animationMouseEnter(reference) {
      this.$refs[reference].play();
    },
    loopComplete(reference) {
      this.$refs[reference].pause();
    },
    async downloadMITRELayer() {
      const layerContent = await this.getStreamData();
      const link = document.createElement('a');
      link.setAttribute('href', layerContent);
      link.setAttribute('download', `mitre-attck-layer-${this.domain.replace('.', '_')}-${Date.now()}.json`);
      document.body.appendChild(link);
      link.click();
    },
    async downloadTwistedDomainsJson() {
      const layerContent = await this.getTwistedDomainsJsonStreamData();
      const link = document.createElement('a');
      link.setAttribute('href', layerContent);
      link.setAttribute('download', `twisted-domains-${this.domain.replace('.', '_')}-${Date.now()}.json`);
      document.body.appendChild(link);
      link.click();
    },
    async getStreamData() {
      const layer = await this.getFilteredMitreData();
      return `data:text/json;charset=utf-8,${encodeURIComponent(JSON.stringify(layer))}`;
    },
    async getFilteredMitreData() {
      const mitre = this.data?.data?.insights?.insightsData?.mitre || {};
      if (this.dataVersion !== 2) return mitre;
      const selectedColor = this.flatAptGroupsColorMap[this.selectedGroup];

      // Filter techniques
      const techniques = (mitre.techniques || []).filter(t => t.color === selectedColor);

      // Filter legendItems
      const legendItems = (mitre.legendItems || []).filter(l => l.color === selectedColor);
      return {
        ...mitre,
        techniques,
        legendItems,
      };
    },
    async getTwistedDomainsJsonStreamData() {
      const jsonContent = `data:text/json;charset=utf-8,${encodeURIComponent(JSON.stringify(this.twistedDomainsFiltered || {}))}`;
      return jsonContent;
    },
    async downloadCSVReport() {
      const csvContent = await this.getTwistedDomainsCsvStreamData();
      const encodedUri = encodeURI(csvContent);
      const link = document.createElement('a');
      link.setAttribute('href', encodedUri);
      link.setAttribute('download', `twisted-domains-${this.domain.replace('.', '_')}-${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
    },
    async getTwistedDomainsCsvStreamData() {
      const rows = [];
      const columnsRow = [
        this.$t('domain.insights.table.header.domain'),
        this.$t('domain.insights.table.header.type'),
        this.$t('domain.insights.table.header.ip.addr'),
        this.$t('domain.insights.table.header.ssdeep'),
        this.$t('domain.insights.table.header.geo'),
      ];
      rows.push(columnsRow);
      const twistedItems = this.twistedDomainsFiltered;
      twistedItems.forEach(value => {
        const currentRow = [];
        currentRow.push(value.domain);
        currentRow.push(value.fuzzer);
        currentRow.push(`"${value.dns_a.join(', ')}"`);
        currentRow.push(value.ssdeep);
        currentRow.push(value.geoip);
        rows.push(currentRow);
      });
      const csvContent = `data:text/csv;charset=utf-8,${
        rows.map(e => e.join(',')).join('\n')}`;
      return csvContent;
    },
    getAPTName(data) {
      if (this.dataVersion === 1) {
        return data.APT_group_display_name;
      }
      if (this.dataVersion === 2) {
        const aptName = data['APT name'];
        // Extract name from "G#### (Name)" format - return just "Name"
        const match = aptName.match(/\(([^)]+)\)/);
        return match ? match[1] : aptName;
      }
      return '';
    },
    getAPTColor(data) {
      if (this.dataVersion === 1) {
        return this.flatAptGroupsColorMap[data.APT_group_display_name];
      }
      if (this.dataVersion === 2) {
        return data.color;
      }
      return '#0000';
    },
    getAPTRiskByName(group) {
      for (let i = 0; i < Object.keys(this.aptRisk).length; i += 1) {
        if (Object.keys(this.aptRisk)[i].includes(group)) {
          return this.aptRisk[Object.keys(this.aptRisk)[i]];
        }
      }
      return {
        '12_months': '0%',
        '3_months': '0%',
        '6_months': '0%',
      };
    },
    getAPTRiskData(data) {
      if (this.dataVersion === 1) {
        return this.getAPTRiskByName(data.APT_group);
      }
      if (this.dataVersion === 2) {
        const risk = data.estimated_percentage_of_getting_hacked || {};
        // Normalize to string format with % for template compatibility
        return {
          '3_months': typeof risk['3_months'] === 'number' ? `${Math.round(risk['3_months'] * 100)}%` : (risk['3_months'] || '0%'),
          '6_months': typeof risk['6_months'] === 'number' ? `${Math.round(risk['6_months'] * 100)}%` : (risk['6_months'] || '0%'),
          '12_months': typeof risk['12_months'] === 'number' ? `${Math.round(risk['12_months'] * 100)}%` : (risk['12_months'] || '0%'),
        };
      }
      return {
        '12_months': '0%',
        '3_months': '0%',
        '6_months': '0%',
      };
    },
    ttpFormatted(ttp) {
      let formattedTTP = ttp.split('(');
      if (Array.isArray(formattedTTP) && formattedTTP.length > 1) {
        [formattedTTP] = formattedTTP;
      } else {
        [formattedTTP] = ttp.split('-');
      }
      return formattedTTP;
    },
    // eslint-disable-next-line no-unused-vars
    formatVector(data) {
      let title = '';
      let subtitle = '';
      if (this.dataVersion === 1) {
        const {vector} = data;
        let splitArray = vector.includes('(CVE') ? vector.split('(CVE') : vector.split('(e.g., CVE');
        if (Array.isArray(splitArray) && splitArray.length > 1) {
          [title] = splitArray;
          subtitle = `CVE${splitArray[1].substring(0, splitArray[1].length - 1)}`;
        } else if (Array.isArray(vector.split(' - ')) && vector.split(' - ').length > 1) {
          splitArray = vector.split(' - ');
          [subtitle, title] = splitArray;
        } else {
          splitArray = vector.split('(Various');
          if (Array.isArray(splitArray) && splitArray.length > 1) {
            [title] = splitArray;
            subtitle = `Various${splitArray[1].substring(0, splitArray[1].length - 1)}`;
          } else {
            [title] = splitArray;
          }
        }
      }
      if (this.dataVersion === 2) {
        title = data.vector;
        // Map CVE IDs to their titles if available
        const cveNames = data.cves.map(cveId => {
          const cveTitle = this.cveIdToTitleMap[cveId];
          return cveTitle ? `${cveId}: ${cveTitle}` : cveId;
        });
        subtitle = cveNames.join(', ');
      }

      // Remove or hide single-word prefixes like "NETWORK", "LOCAL", etc.
      if (title) {
        // If title is just a single all-caps word, hide it completely
        if (/^[A-Z]+$/.test(title.trim())) {
          title = '';
        } else {
          // Otherwise, remove leading single all-caps words (NETWORK, LOCAL, PHYSICAL, etc.)
          title = title.replace(/^[A-Z]+\s+/, '');
        }
      }

      return {title, subtitle};
    },
    updateScrollBar() {
      this.$nextTick(() => {
        if (!this.noData) {
          if (this.scrollbars) {
            this.scrollbars.forEach(scroller => scroller.update());
          } else {
            const selector = '.domain-insights__mitre-scroll-area';
            const els = document.querySelectorAll(selector);
            this.scrollbars = [];
            els.forEach(el => this.scrollbars.push(new PerfectScrollbar(el, this.settings)));
          }
        }
      });
    },
    async initiateDiscoveryTest() {
      this.initiateTestClicked = true;
      const selectedTimeZone = this.formattedTimeZone.find(timezone => timezone.value === String(moment().utcOffset()));
      try {
        const test = {
          url: this.domain,
          tld: this.domain,
          type: TEST_TYPES_TLDS.discovery,
          schedule: false,
          isAllDay: false,
          startDate: moment().format(),
          startTime: getTimeWithMinuteOffset(2),
          endTime: getTimeWithHourOffset(2),
          timezone: selectedTimeZone.id,
          customRepeatData: {},
          frequency: TEST_REPEAT_STATUS.never,
        };
        // eslint-disable-next-line no-await-in-loop
        const createdTest = await this.$store.dispatch(ADD_NEW_TEST_PROJECT, test);
        if (createdTest) {
          try {
            // eslint-disable-next-line no-await-in-loop
            await this.$store.dispatch(INITIATE_TEST_CARD, [createdTest.id]);
          } catch (e) {
            this.errorMessage = e.message;
          }
        }
      } catch (e) {
        this.errorMessage = e.message;
      }

      this.testInitiated = true;
    },
    isValid(value) {
      let valid = true;
      value.forEach(item => {
        if (!rules.hostWithRange(item)) {
          valid = false;
        }
      });
      return valid;
    },
    getGeoLocation(location) {
      const dataGeo = JSON.parse(WorldGeoJson);
      let country = location;
      if (country === 'United States') {
        country = 'USA';
      }
      if (country === 'United Kingdom') {
        country = 'England';
      }
      const result = dataGeo.features.find(item => item.properties.name === country)?.id || '';
      return result;
    },
    hasReasoning(group) {
      return group.reasoning && Object.keys(group.reasoning).length > 0;
    },
    toggleGroupReasoning(groupName) {
      this.$set(this.flippedGroups, groupName, !this.flippedGroups[groupName]);
    },
    formatReasoningSummary(summary) {
      if (!summary) return '';
      // Convert markdown-style formatting to HTML
      let formatted = summary
        // Convert headers (# Title)
        .replace(/^# (.+)$/gm, '<h4 class="reasoning-heading">$1</h4>')
        // Convert bold (**text**)
        .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
        // Convert line breaks
        .replace(/\n\n/g, '</p><p>')
        .replace(/\n/g, '<br>');
      // Wrap in paragraph if not already
      if (!formatted.startsWith('<')) {
        formatted = `<p>${formatted}</p>`;
      }
      return formatted;
    },
  },
};
</script>

<style lang='scss' scoped>
@import '~@/assets/styles/design-system';

.domain-insights {
  display: flex;
  flex-direction: column;
  margin-right: rem(32);
  margin-left: rem(32);
  padding-bottom: rem(50);
  &__apt-group-select{
    max-width: rem(270);
    min-width: rem(150);
    margin-bottom: rem(20);
    margin-top: rem(-22);
    &__item{
      display: flex;
      flex-direction: row;
      align-items: center;
      .color-circle{
        height: rem(12);
        width: rem(12);
        border-radius: $radius-xs;
        margin-right: rem(10);
      }
    }
  }
  &__content {
  }
  &__table-wrapper {
  }
  &__title{
    text-transform: uppercase;
    margin-bottom: rem(30);
    color: var(--text-primary);
    font-family: $font-family-bold;
    font-size: rem(22);
    font-weight: bold;
    line-height: rem(20);
    letter-spacing: 0;
  }
  &__section-header{
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    width: 100%;
    &__controls{
      display: flex;
      flex-direction: row;
      justify-content: right;
    }
    .control-animation-wrapper{
      &:hover {
        cursor: pointer;
      }
    }
    .control-animation{
      height: rem(48);
    }
  }
  &__subtitle {
    color: var(--text-primary);
    font-family: $font-family-bold;
    font-size: rem(16);
    font-weight: bold;
    line-height: rem(20);
    letter-spacing: 0;
    margin-bottom: rem(32);
  }
  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    &__left {
      display: flex;
      align-items: center;
      &__subtitle{
        color: var(--text-primary);
        font-family: $font-family-bold;
        font-size: rem(16);
        font-weight: bold;
        line-height: rem(20);
        letter-spacing: 0;
        margin-bottom: rem(32);
        margin-top: rem(30);
      }
      &__switch {
        margin-left: rem(60);
      }
    }
    &__right {
      display: flex;
      align-items: center;
      &__json {
        cursor: pointer;
        .control-animation {
          height: rem(48);
        }
      }
      &__csv {
        color: var(--text-secondary);
        font-family: $font-family-medium;
        font-size: rem(12);
        height: rem(32);
        letter-spacing: rem(1.1);
        line-height: rem(16);
        width: rem(32);
        &:hover {
          cursor: pointer;
        }
      }
    }
  }
  &__mitre-scroll-area{
    height: auto;
    position: relative;
  }
  &__table{
    display: flex;
    flex-direction: column;
    max-width: 100%;
    margin-top: rem(20);
    margin-bottom: rem(20);
    &__header-row {
      display: flex;
      flex-direction: row;
      border-bottom: 1px solid var(--border-primary);
    }
    &__row {
      display: flex;
      flex-direction: row;
      border-bottom: 1px solid var(--border-primary);
      min-height: rem(52);
      padding: rem(5) 0;
      height: fit-content;
      align-items: center;

      &.extra-height {
        height: rem(60);
        overflow: hidden;
      }

      .apt-name {
        display: flex;
        flex-basis: 70%;
      }

      .apt-colors {
        display: flex;
        justify-content: left;
        flex-direction: row;
        align-items: center;
        flex-basis: 30%;

        .color-circle {
          height: rem(12);
          width: rem(12);
          border-radius: $radius-xs;
          margin-left: rem(5);
        }
      }
      .twisted-domains-ip-list {
        white-space: pre-line;
      }
      &__flag {
        margin-right: rem(10);
        margin-left: rem(-12);
      }
      &__country {
        margin-top: rem(10);
      }
      .domain-data-text, .ip-data-text {
        cursor: pointer;
        line-height: 1.5;
      }
    }
    .extra-row-15{
      height: fit-content !important;
    }
    .header-cell {
      font-family: $font-family-regular;
      font-size: rem(12);
      letter-spacing: rem(1.1);
      line-height: rem(15);
      color: var(--text-secondary);
      text-transform: uppercase;
      margin-bottom: rem(15);
      display: flex;
    }
    .table-cell{
      display: flex;
      flex-flow: wrap;
      flex-direction: row;
      margin-right: rem(20);
      .attack-vectors-row-tooltip {
        width: fit-content;
      }
      &.space-between{
        justify-content: space-between;
      }
      .ttp-chip {
        margin-right: rem(5);
        margin-bottom: rem(5);
        margin-top: rem(3);
      }
      &__title{
        color: var(--text-primary);
        font-family: $font-family-bold;
        font-size: rem(14);
        font-weight: bold;
        letter-spacing: 0;
        line-height: rem(17);
        margin-bottom: rem(3);
      }
      &__subtitle{
        color: var(--text-primary);
        font-family: $font-family-regular;
        font-size: rem(12);
        letter-spacing: 0;
        line-height: rem(15);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: rem(300);
      }
      &.damage-cell {
        display: block;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 100%;
      }
    }
    .damage-cell-tooltip {
      flex-basis: 85%;
      max-width: 85%;
      overflow: hidden;
      cursor: pointer;
    }

    // Ensure vector and damage cells are left-aligned
    .c-15.table-cell,
    .c-85.table-cell,
    .c-15.header-cell,
    .c-85.header-cell {
      justify-content: flex-start;
      text-align: left;
    }

    .centered{
      justify-content: center !important;
    }
    .vertical {
      flex-direction: column;
    }
    .header-cell-vertical-container{
      display: flex;
      flex-direction: column;
    }
    .header-cell-container, .table-cell-container{
      display: flex;
      flex-direction: row;
    }
    .l-10 {
      margin-left: rem(-18);
    }
    .l-20 {
      margin-left: -4.8%;
    }
    .r-6 {
      margin-left: rem(6);
    }
    .l-5 {
      padding-left: rem(7);
    }
    .l-12 {
      padding-left: rem(15);
    }
    .r-17 {
      padding-left: rem(22);
    }
    .c-30 {
      flex-basis: 30%;
    }
    .c-40 {
      flex-basis: 40%;
    }
    .c-60 {
      flex-basis: 60%;
    }
    .c-20 {
      flex-basis: 20%;
    }
    .c-18 {
      flex-basis: 18%;
    }
    .c-10 {
      flex-basis: 10%;
    }
    .c-25 {
      flex-basis: 25%;
    }
    .c-70 {
      flex-basis: 70%;
    }
    .c-33{
      flex-basis: 33%;
    }
    .c-13{
      flex-basis: 11.5%;
    }
    .c-15{
      flex-basis: 15%;
    }
    .c-85 {
      flex-basis: 85%;
    }
    .screenshot-cell {
      display: flex;
      align-items: center;
      .screenshot-thumbnail {
        cursor: pointer;
        border: 1px solid var(--text-secondary);
        border-radius: $radius-xs;
        max-width: rem(97);
        max-height: rem(77);
        min-width: rem(95);
        min-height: rem(75);
        &:hover {
          border-color: var(--text-secondary);
        }
      }
    }
  }
  .no-data {
    text-align: center;
    color: var(--text-muted);
    font-family: $font-family-regular;
    font-size: rem(30);
    line-height: rem(40);
    min-width: rem(650);
    display: flex;
    justify-content: center;
    margin-top: rem(90);
    flex-direction: column;
    &__subtitle {
      font-family: $font-family-regular;
      font-size: rem(16);
    }
    &__initiate-test {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      .scan-initiated {
        color: var(--text-primary);
        font-family: $font-family-semibold;
        font-size: rem(12);
        text-transform: uppercase;
      }
      .initiate-scan-btn {
        width: fit-content;
        padding: rem(3) rem(5);
        background-color: var(--brand-primary) !important;
        border-radius: $radius-xs;
        box-shadow: 0 rem(1) rem(1) 0 rgba(0, 0, 0, 0.14), 0 rem(2) rem(1) rem(-1) rgba(0, 0, 0, 0.12), 0 rem(1) rem(3) 0 rgba(0, 0, 0, 0.2);
        color: var(--text-inverse);
        font-family: $font-family-medium;
        font-size: rem(12);
        font-weight: 500;
        letter-spacing: rem(1.1);
        line-height: rem(16);
        text-align: center;
        margin-bottom: rem(20);
      }
    }
  }

  // APT Row Flip Container and Reasoning Card Styles
  .apt-row-flip-container {
    position: relative;
    border-bottom: 1px solid var(--border-primary);
  }

  .apt-name-actions {
    display: flex;
    align-items: center;
    gap: rem(8);
  }

  .reasoning-btn {
    opacity: 0.8;
    transition: all 0.2s ease;
    &:hover {
      opacity: 1;
      background: var(--reasoning-btn-hover-bg);
    }
  }

  .apt-reasoning-card {
    display: none;
    background: var(--reasoning-card-bg);
    border: 1px solid var(--reasoning-card-border);
    border-radius: $radius-md;
    margin: rem(8) 0;
    padding: rem(16);
    animation: slideDown 0.3s ease;

    &.visible {
      display: block;
    }
  }

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(rem(-10));
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .reasoning-card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: rem(16);
    padding-bottom: rem(12);
    border-bottom: 1px solid var(--reasoning-header-border);
  }

  .reasoning-card-title {
    display: flex;
    align-items: center;
    font-size: rem(14);
    font-weight: 600;
    color: var(--reasoning-accent);
  }

  .reasoning-card-content {
    max-height: rem(400);
    overflow-y: auto;
  }

  .reasoning-section {
    margin-bottom: rem(16);

    &:last-child {
      margin-bottom: 0;
    }
  }

  .reasoning-section-title {
    font-size: rem(11);
    font-weight: 600;
    color: var(--text-secondary);
    text-transform: uppercase;
    letter-spacing: rem(0.5);
    margin-bottom: rem(8);
  }

  .threat-score-bar {
    position: relative;
    height: rem(24);
    background: var(--reasoning-score-bar-bg);
    border-radius: rem(12);
    overflow: hidden;
  }

  .threat-score-fill {
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    background: var(--reasoning-score-fill-gradient);
    border-radius: rem(12);
    transition: width 0.5s ease;
  }

  .threat-score-value {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    font-size: rem(12);
    font-weight: 600;
    color: var(--reasoning-score-text);
    text-shadow: 0 rem(1) rem(2) rgba(0, 0, 0, 0.5);
  }

  .overlap-stats-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: rem(8);
  }

  .overlap-stat-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: rem(8) rem(12);
    background: var(--reasoning-stat-bg);
    border-radius: $radius-sm;
  }

  .overlap-label {
    font-size: rem(10);
    color: var(--text-secondary);
    margin-bottom: rem(4);
  }

  .overlap-value {
    font-size: rem(16);
    font-weight: 600;
    color: var(--reasoning-accent);
  }

  .reasoning-summary-section {
    margin-top: rem(16);
  }

  .reasoning-summary-text {
    font-size: rem(13);
    line-height: 1.7;
    color: var(--text-primary);
    max-height: rem(200);
    overflow-y: auto;
    padding-right: rem(8);

    p {
      margin-bottom: rem(12);

      &:last-child {
        margin-bottom: 0;
      }
    }

    strong {
      color: var(--reasoning-accent);
    }

    .reasoning-heading {
      font-size: rem(14);
      font-weight: 600;
      color: var(--text-primary);
      margin: rem(16) 0 rem(8) 0;

      &:first-child {
        margin-top: 0;
      }
    }
  }
}
</style>

<style lang='scss'>
@import '~@/assets/styles/design-system';

.domain-insights {
  .manual-config-badge {
    .v-badge__wrapper {
      .v-badge__badge {
        background: transparent !important;
        border: none !important;
        color: var(--text-secondary);
        .v-icon {
          font-size: rem(16);
          transform: rotate(180deg);
        }
      }
    }
    &.copied-badge {
      .v-badge__wrapper {
        .v-badge__badge {
          text-transform: capitalize;
          background: transparent !important;
          border: none !important;
          color: var(--text-secondary);
          right: rem(20) !important;
          inset: auto auto calc(100% - #{rem(1)}) calc(100% - #{rem(30)}) !important;
        }
      }
    }
  }
}

.dns-twist-screenshot-dialog {
  .screenshot-popup-card {
    padding: rem(16);
    .screenshot-popup-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: rem(16);
      .screenshot-domain {
        font-family: $font-family-bold;
        font-size: rem(16);
        color: var(--text-primary);
      }
      .close-btn {
        cursor: pointer;
        &:hover {
          color: var(--brand-primary);
        }
      }
    }
    .screenshot-content {
      display: flex;
      justify-content: center;
      max-height: 80vh;
      overflow: auto;
      img {
        max-width: 100%;
        height: auto;
      }
    }
  }
}

.damage-tooltip {
  max-width: rem(500) !important;
  .damage-tooltip-content {
    font-size: rem(13);
    line-height: 1.5;
    white-space: pre-wrap;
    word-break: break-word;
  }
}

// TTP Chip styling for APT Groups table
.domain-insights {
  .table-cell {
    .ttp-chip.v-chip.v-chip {
      background: var(--bg-tertiary) !important;
      color: var(--text-primary) !important;
      border-radius: rem(12) !important;
      height: rem(26) !important;
      font-size: rem(11) !important;
      font-family: $font-family-medium !important;
      padding: 0 rem(10) !important;
      margin: rem(3) rem(5) rem(3) 0 !important;
      border: 1px solid var(--border-primary) !important;

      .v-chip__content {
        padding: 0;
      }
    }
  }

  // Fix column alignment - give APT name column more space
  .domain-insights__table__row {
    .table-cell.c-20:first-child {
      flex-basis: 22%;
      min-width: rem(180);
      padding-right: rem(10);
    }
  }

  // Override threat-visualizer gray background to white in light mode
  .threat-visualizer.threat-visualizer {
    background: #ffffff;
  }
}

[data-theme="dark"] {
  .domain-insights {
    .threat-visualizer.threat-visualizer {
      background: var(--bg-secondary);
    }
  }
}
</style>
